// "Estudos em números": KPIs e gráficos do módulo Estudos (rede) e da lista de centros. Tudo vem de
// `GET /network/studies/overview` e `GET /network/tenants-overview`; sem dados → EmptyState (REGRA 01), nunca exemplo.
import { Activity, FileText, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { BarChart, ChartCard, DonutChart, KpiCard } from '@giyus/patterns';
import { Alert, AlertDescription, EmptyState } from '@giyus/ui';

import { LINK_CLASS } from './AttentionPanel';
import { dossiersInProgress, enrollmentPercent, toNumber, toSegments } from './homeData';
import type { HomeScope, HomeSignals } from './useHomeSignals';

export interface StudyKpisProps {
  readonly display?: 'full' | 'summary' | 'charts';
  readonly scope: HomeScope;
  readonly basePath: string;
  readonly signals: HomeSignals;
}

export function StudyKpis({ scope, basePath, signals, display = 'full' }: StudyKpisProps) {
  const { t } = useTranslation(['shell', 'common']);
  const text = (key: string, values?: Record<string, unknown>): string =>
    t(`shell:home.dashboard.kpis.${key}` as never, values as never) as unknown as string;
  const loadingLabel = t('common:state.loading');

  // Execução (visitas, participantes): sem fonte persistida — uma explicação honesta, em qualquer escopo.
  const execution = (
    <ChartCard
      title={text('executionTitle')}
      description={text('executionDescription')}
      headingLevel="h3"
      emptyState={
        <EmptyState
          icon={<Activity />}
          title={text('executionEmptyTitle')}
          description={text('executionEmptyHint')}
          headingLevel="h4"
        />
      }
    />
  );

  if (scope === 'tenant') {
    if (display === 'summary') return null;
    return (
      <section
        aria-labelledby="home-kpis-title"
        className="flex flex-col gap-3"
        data-testid="home-study-kpis"
      >
        <h2 id="home-kpis-title" className="text-title font-semibold text-heading">
          {text('title')}
        </h2>
        <div className="grid gap-3 lg:grid-cols-2">
          <ChartCard
            title={text('tenantTitle')}
            headingLevel="h3"
            emptyState={
              <EmptyState
                icon={<FileText />}
                title={text('tenantEmptyTitle')}
                description={text('tenantEmptyHint')}
                headingLevel="h4"
              />
            }
          />
          {execution}
        </div>
      </section>
    );
  }

  if (!signals.access.studies) {
    return null;
  }

  const query = signals.studies;
  const data = query.data;
  const total = toNumber(data?.totalStudies);
  const target = toNumber(data?.targetEnrollmentTotal);
  const current = toNumber(data?.currentEnrollmentTotal);
  const dossiers = toNumber(data?.totalDossiers);
  const inProgress = dossiersInProgress(data?.dossiersByStatus ?? []);
  const unassigned = toNumber(data?.unassignedMailboxMessages);
  const loading = query.isPending;

  const statusSegments = toSegments(data?.studiesByStatus ?? [], (status) =>
    text(`studyStatus.${status}`),
  );
  const dossierSegments = toSegments(data?.dossiersByStatus ?? [], (status) =>
    text(`dossierStatus.${status}`),
  );
  const enrollmentItems = (data?.enrollment ?? []).map((study) => ({
    id: study.code,
    label: `${study.code} — ${study.title}`,
    value: toNumber(study.currentEnrollment),
    target: toNumber(study.targetEnrollment),
  }));
  const percent = enrollmentPercent(current, target);

  const empty = (title: string, hint: string, icon: React.ReactNode) => (
    <EmptyState icon={icon} title={title} description={hint} headingLevel="h4" />
  );

  if (display === 'summary')
    return (
      <section aria-label={text('title')} data-testid="home-study-kpis">
        {query.isError ? (
          <Alert variant="destructive">
            <AlertDescription>{text('error')}</AlertDescription>
          </Alert>
        ) : loading ? (
          <p role="status">{loadingLabel}</p>
        ) : (
          <div
            className="grid gap-px overflow-hidden rounded-card border border-border bg-border sm:grid-cols-2 xl:grid-cols-4 [&>[data-slot=kpi-card]]:rounded-none [&>[data-slot=kpi-card]]:border-0 [&>[data-slot=kpi-card]]:shadow-none"
            data-testid="home-kpi-cards"
          >
            <KpiCard label={text('studies')} value={total} />
            <KpiCard
              label={text('enrollment')}
              value={text('enrollmentValue', { current, target })}
              description={target > 0 ? text('enrollmentPercent', { percent }) : undefined}
            />
            <KpiCard
              label={text('dossiers')}
              value={dossiers}
              description={text('dossiersHint', { count: inProgress })}
            />
            <KpiCard
              label={text('unassigned')}
              value={unassigned}
              description={text('unassignedHint')}
              tone={unassigned > 0 ? 'warning' : 'neutral'}
              {...(unassigned > 0
                ? { toneLabel: t('shell:home.dashboard.attention.pending') }
                : {})}
            />
          </div>
        )}
      </section>
    );

  return (
    <section
      aria-labelledby="home-kpis-title"
      className="flex flex-col gap-3"
      data-testid={display === 'charts' ? 'home-study-charts' : 'home-study-kpis'}
    >
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div className="flex flex-col gap-1">
          <h2 id="home-kpis-title" className="text-title font-semibold text-heading">
            {text('title')}
          </h2>
          <p className="text-body-sm text-muted-foreground">{text('description')}</p>
        </div>
        <Link to={`${basePath}/studies`} className={`${LINK_CLASS} text-body-sm`}>
          {text('viewStudies')}
        </Link>
      </div>

      {query.isError ? (
        <Alert variant="destructive">
          <AlertDescription>{text('error')}</AlertDescription>
        </Alert>
      ) : (
        <>
          {!loading && display !== 'charts' ? (
            <div
              className="grid gap-px overflow-hidden rounded-card border border-border bg-border sm:grid-cols-2 xl:grid-cols-4 [&>[data-slot=kpi-card]]:rounded-none [&>[data-slot=kpi-card]]:border-0 [&>[data-slot=kpi-card]]:shadow-none"
              data-testid="home-kpi-cards"
            >
              <KpiCard label={text('studies')} value={total} />
              <KpiCard
                label={text('enrollment')}
                value={text('enrollmentValue', { current, target })}
                description={target > 0 ? text('enrollmentPercent', { percent }) : undefined}
              />
              <KpiCard
                label={text('dossiers')}
                value={dossiers}
                description={text('dossiersHint', { count: inProgress })}
              />
              <KpiCard
                label={text('unassigned')}
                value={unassigned}
                description={text('unassignedHint')}
                tone={unassigned > 0 ? 'warning' : 'neutral'}
                {...(unassigned > 0
                  ? { toneLabel: t('shell:home.dashboard.attention.pending') }
                  : {})}
              />
            </div>
          ) : null}

          <div className="grid gap-3 lg:grid-cols-2">
            <ChartCard
              title={text('statusChart')}
              headingLevel="h3"
              loading={loading}
              loadingLabel={loadingLabel}
              emptyState={
                !loading && total === 0
                  ? empty(text('emptyStudiesTitle'), text('emptyStudiesHint'), <FileText />)
                  : undefined
              }
            >
              <DonutChart
                label={text('statusChartLabel')}
                totalLabel={text('statusTotal')}
                segments={statusSegments}
                formatSegment={(segment, pct) =>
                  text('segment', { value: segment.value, percent: Math.round(pct) })
                }
              />
            </ChartCard>

            <ChartCard
              title={text('enrollmentChart')}
              description={text('enrollmentChartDescription')}
              headingLevel="h3"
              loading={loading}
              loadingLabel={loadingLabel}
              emptyState={
                !loading && enrollmentItems.length === 0
                  ? empty(text('emptyEnrollmentTitle'), text('emptyEnrollmentHint'), <Users />)
                  : undefined
              }
            >
              <BarChart
                label={text('enrollmentChartLabel')}
                items={enrollmentItems}
                formatValue={(item) =>
                  text('enrollmentValue', { current: item.value, target: item.target ?? 0 })
                }
              />
            </ChartCard>

            <ChartCard
              title={text('dossierChart')}
              headingLevel="h3"
              loading={loading}
              loadingLabel={loadingLabel}
              emptyState={
                !loading && dossiers === 0
                  ? empty(text('emptyDossiersTitle'), text('emptyDossiersHint'), <FileText />)
                  : undefined
              }
            >
              <DonutChart
                label={text('dossierChartLabel')}
                totalLabel={text('dossierTotal')}
                segments={dossierSegments}
                formatSegment={(segment, pct) =>
                  text('segment', { value: segment.value, percent: Math.round(pct) })
                }
              />
            </ChartCard>

            <TenantsChart signals={signals} />
          </div>
        </>
      )}
      {execution}
    </section>
  );
}

/** Ordem fixa do ciclo de vida: cada situação mantém sua cor nas rosquetas. */
const TENANT_STATUS_ORDER = [
  'Draft',
  'Onboarding',
  'Active',
  'Suspended',
  'Offboarding',
  'Offboarded',
] as const;

function TenantsChart({ signals }: { readonly signals: HomeSignals }) {
  const { t } = useTranslation(['shell', 'common']);
  const text = (key: string): string => t(`shell:home.dashboard.tenants.${key}` as never);
  if (!signals.access.tenants) {
    return null;
  }
  const query = signals.tenants;
  const items = query.data?.items ?? [];
  const counts = new Map<string, number>(TENANT_STATUS_ORDER.map((status) => [status, 0]));
  for (const tenant of items) {
    counts.set(tenant.status, (counts.get(tenant.status) ?? 0) + 1);
  }
  const segments = toSegments(
    [...counts].map(([status, count]) => ({ status, count })),
    (status) =>
      t(
        `shell:home.dashboard.tenants.status.${status}` as never,
        {
          defaultValue: status,
        } as never,
      ) as unknown as string,
  );
  return (
    <ChartCard
      title={text('chart')}
      headingLevel="h3"
      loading={query.isPending}
      loadingLabel={t('common:state.loading')}
      emptyState={
        query.isError ? (
          <Alert variant="destructive">
            <AlertDescription>{text('error')}</AlertDescription>
          </Alert>
        ) : !query.isPending && items.length === 0 ? (
          <EmptyState
            title={text('emptyTitle')}
            description={text('emptyHint')}
            headingLevel="h4"
          />
        ) : undefined
      }
    >
      <DonutChart label={text('chartLabel')} totalLabel={text('total')} segments={segments} />
    </ChartCard>
  );
}
